import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiHeart, FiMessageCircle, FiShare2, FiArrowRight, FiCornerDownRight, FiMoreHorizontal } from 'react-icons/fi';
import { relativeTime } from '../utils/time';
import { likeMatchup, unlikeMatchup, likeBracket, unlikeBracket, likeTierList, unlikeTierList } from '../services/api';
import { track } from '../utils/analytics';
import { notifyActionFailed } from '../utils/actionError';
import { useAnonUpgradePrompt } from '../contexts/AnonUpgradeContext';
import { buildShareUrl, withRef } from '../utils/shareUrl';
// HomeCard's styles live in HomePage.css (all .home-* scoped, no global
// bleed). Import here so the card is fully styled anywhere it's reused
// — e.g. the profile tabs — not just on /home.
import '../styles/HomePage.css';

// Calmer card-fallback gradients. Lower saturation than the
// previous palette so they read as quiet placeholders rather than
// decorative-noise — research feedback called the original 8-hue
// rotation out as competing with the actual content. All tints
// land in the same indigo / violet / slate cool range so the card
// grid feels cohesive even when no thumbnails have been uploaded.
const GRADIENTS = [
  'linear-gradient(135deg, rgba(99, 102, 241, 0.55), rgba(15, 23, 42, 0.95))',
  'linear-gradient(135deg, rgba(167, 139, 250, 0.5),  rgba(15, 23, 42, 0.95))',
  'linear-gradient(135deg, rgba(96, 165, 250, 0.55),  rgba(15, 23, 42, 0.95))',
  'linear-gradient(135deg, rgba(244, 114, 182, 0.45), rgba(15, 23, 42, 0.95))',
];

// Tag rules — order matters. The first rule whose keyword list hits
// the lowercased title contributes a tag (deriveTags returns up to 2
// matches). Specific subgenres come BEFORE broader categories so a
// title like "Best Pokémon Game" tags as Pokémon (not Gaming) and
// "K-Pop Song of the Year" tags as K-Pop (not Music).
//
// Tag strings must match the labels in HomeSidebar's CATEGORIES list
// — HomePage.js filters with an exact `tags.includes(categoryFilter)`.
const TAG_RULES = [
  // Specific subgenres (must precede the broader category they fall under).
  { keywords: ['pokemon', 'pokémon', 'pikachu', 'pokeball'], tag: 'Pokémon' },
  { keywords: ['k-pop', 'kpop', 'bts', 'blackpink', 'twice', 'newjeans'], tag: 'K-Pop' },
  { keywords: ['manga', 'manhwa', 'manhua', 'webtoon'], tag: 'Manga' },
  { keywords: ['anime', 'shonen', 'shoujo', 'op', 'ending'], tag: 'Anime' },
  { keywords: ['cartoon', 'looney', 'spongebob', 'simpsons', 'family guy', 'rick and morty', 'animated series'], tag: 'Cartoons' },

  // Broader categories.
  { keywords: ['music', 'song', 'songs', 'album', 'rapper', 'singer', 'band', 'artist', 'r&b', 'hip hop', 'rap'], tag: 'Music' },
  { keywords: ['movie', 'film', 'marvel', 'dc', 'cinema'], tag: 'Movies' },
  { keywords: ['tv', 'show', 'series', 'sitcom', 'episode', 'season'], tag: 'TV Shows' },
  { keywords: ['game', 'gaming', 'mario', 'xbox', 'playstation', 'nintendo', 'video game', 'rpg', 'fps'], tag: 'Gaming' },
  { keywords: ['sport', 'nba', 'nfl', 'soccer', 'football', 'basketball', 'baseball', 'goat', 'player'], tag: 'Sports' },
  { keywords: ['food', 'pizza', 'burger', 'taco', 'sushi', 'pasta', 'ramen', 'cuisine', 'restaurant', 'dish'], tag: 'Food' },
  { keywords: ['animal', 'dog', 'cat', 'pet', 'puppy', 'kitten', 'wolf', 'lion', 'bear'], tag: 'Animals' },
  { keywords: ['celebrity', 'celeb', 'actor', 'actress', 'kardashian', 'taylor swift', 'beyonce', 'rihanna'], tag: 'Celebrities' },
];

// Returns up to 2 tags for a title. When no rule fires we hand back
// ['Other'] — the sidebar pins an "Other" category as the catch-all,
// and the HomePage filter does an exact `tags.includes(categoryFilter)`,
// so the card display and the filter MUST share this vocabulary or
// the "Other" filter would never match anything.
export function deriveTags(title) {
  if (!title) return ['Other'];
  const lower = title.toLowerCase();
  const tags = [];
  for (const rule of TAG_RULES) {
    if (rule.keywords.some((kw) => lower.includes(kw))) {
      tags.push(rule.tag);
      if (tags.length === 2) break;
    }
  }
  return tags.length > 0 ? tags : ['Other'];
}

function titleGradient(title) {
  if (!title) return GRADIENTS[0];
  let hash = 0;
  for (let i = 0; i < title.length; i++) {
    hash = (hash * 31 + title.charCodeAt(i)) | 0;
  }
  return GRADIENTS[Math.abs(hash) % GRADIENTS.length];
}

// `relativeTime` now lives in utils/time.js so ActivityFeed + others
// can share it. Re-imported rather than duplicated.

function authorDisplay(item) {
  if (item.author?.username) return item.author.username;
  if (item.author_username) return item.author_username;
  const id = item.author_id ?? item.bracket_author_id;
  if (id) return id.slice(0, 8);
  return 'unknown';
}

function authorInitial(item) {
  const name = authorDisplay(item);
  return name.charAt(0).toUpperCase();
}

function withImageSize(url, size) {
  if (!url || !size) return url;
  return url.replace(/\.(jpe?g|png|gif|webp)$/i, `_${size}.jpg`);
}

const HomeCard = ({ item, type, initialLiked = false, fallbackGradient = null, menuActions = [] }) => {
  const navigate = useNavigate();
  const { promptUpgrade } = useAnonUpgradePrompt();
  const viewerId = typeof window !== 'undefined'
    ? window.localStorage.getItem('userId')
    : null;

  // Owner-only overflow menu (⋯) — carries manage actions (Archive /
  // Delete / Unsave) on the owner's profile cards that the feed card
  // doesn't have. Empty list (the feed default) → no menu rendered.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);
  const title = item.title || (type === 'bracket' ? 'Untitled Bracket' : type === 'tierlist' ? 'Untitled Tier List' : type === 'pool' ? 'Untitled Pool' : 'Untitled Matchup');
  const backendTags = Array.isArray(item.tags) && item.tags.length > 0 ? item.tags : null;
  // deriveTags() now guarantees at least one tag ('Other' as the
  // catch-all) so we can drop the previous in-component fallback —
  // and the chip we render is guaranteed to be a sidebar-known
  // category, so filtering on it works.
  const tags = backendTags ?? deriveTags(title);
  // `fallbackGradient` overrides the deterministic title-based pick.
  // Used by CommunityPage to render the community's chosen theme on
  // every card-without-image inside the community feed — so the
  // cards visually belong to the themed community instead of falling
  // through to one of the violet/blue placeholders. Empty / null
  // means "use the default behaviour" so home, profile, search etc.
  // are unaffected.
  const gradient = fallbackGradient || titleGradient(title);
  // Cover media — request the 900w `_large` variant for Retina-quality
  // rendering at the card's ~400-600px display width. Pre-PR-63
  // uploads don't have a `_large` S3 object; handleCoverImgError below
  // falls back to `_medium` then `_thumb` so legacy assets degrade
  // gracefully without 404'ing the slot.
  const imageUrl = withImageSize(item.image_url ?? null, 'large');
  const imageFallbacks = item.image_url
    ? [
        withImageSize(item.image_url, 'medium'),
        withImageSize(item.image_url, 'thumb'),
      ]
    : [];
  const author = authorDisplay(item);
  const initial = authorInitial(item);
  const timeAgo = relativeTime(item.created_at);
  // Author profile picture. Two shapes flow through HomeCard:
  //   1. Full MatchupData / BracketData — nested item.author.avatar_path
  //      (added to common.v1.UserSummaryResponse).
  //   2. PopularMatchupData / PopularBracketData — flat
  //      item.author_avatar_path (the trending / most-played feeds
  //      use the lightweight shape, no nested author).
  // We check both so the avatar renders regardless of which path the
  // card came from. Both shapes carry the already-resolved full URL.
  // Falls through to the letter initial on the gradient pill when
  // neither is set — same behaviour as before.
  const authorAvatarRaw = item.author?.avatar_path || item.author_avatar_path || '';
  const authorAvatarUrl = authorAvatarRaw
    ? withImageSize(authorAvatarRaw, 'thumb')
    : null;
  const authorId = item.author_id ?? item.bracket_author_id;
  const initialLikesCount = item.likes_count ?? item.likes ?? 0;
  const commentsCount = Array.isArray(item.comments) ? item.comments.length : (item.comments ?? 0);
  // Vote count derivation. The home feed pulls items from different
  // RPCs and each shape carries the total in a different field:
  //   - PopularMatchupData / PopularBracketData → flat `votes` int
  //   - MatchupData (Latest tab → ListMatchups) → no top-level votes,
  //                                                only per-item `items[].votes`
  //   - BracketData                             → `total_votes` (summed
  //                                                across child matchups)
  // The prior `item.votes ?? 0` dropped to "0 votes" on the Latest tab
  // and on every bracket card. Order matters: top-level wins over the
  // per-item sum so a backend snapshot isn't overwritten by partial
  // data when the items array is missing.
  const votesCount = (() => {
    if (typeof item.votes === 'number' && item.votes > 0) return item.votes;
    if (typeof item.total_votes === 'number' && item.total_votes > 0) return item.total_votes;
    if (Array.isArray(item.items)) {
      return item.items.reduce((s, it) => s + (Number(it?.votes) || 0), 0);
    }
    return Number(item.votes ?? item.total_votes ?? 0) || 0;
  })();

  // Status flag — feeds it through to the badge + caption affordances.
  // Standalone matchups carry a "completed" status when their voting
  // closes; brackets follow the same pattern. Used to surface a small
  // "Completed" pill so the home feed reads as a mix of active +
  // resolved content instead of a sea of identical cards.
  const status = (item.status || '').toLowerCase();
  const isCompleted = status === 'completed';

  // Per-item vote breakdown — gives the home card more "things are
  // happening" energy than a single "X votes" number. We pull the
  // first two items (matchups have exactly two by design; brackets
  // have N children so the top-level bracket card sums the kids
  // and the per-item breakdown is N/A — gracefully renders nothing).
  const matchupItems = type === 'matchup' && Array.isArray(item.items)
    ? item.items.slice(0, 2)
    : [];
  const itemBreakdownPair = matchupItems.length === 2
    ? matchupItems.map((it) => ({
        label: it.item ?? it.name ?? '?',
        votes: Number(it.votes ?? it.Votes ?? 0),
        userUsername: it.user_username || it.userUsername || null,
      }))
    : null;

  // Interactive state for the action row. Initial liked-state arrives
  // as a prop (HomePage fetches the viewer's like list once on mount
  // and passes the relevant flag down). Optimistic + reversible —
  // failures roll the heart and count back. `burstKey` increments on
  // every like to remount the burst element so the CSS keyframe
  // re-runs (using `key={burstKey}` instead of toggling a class
  // sidesteps the "browser caches the animation state" gotcha).
  const [liked, setLiked] = useState(Boolean(initialLiked));
  const [likesCount, setLikesCount] = useState(initialLikesCount);
  const [likePending, setLikePending] = useState(false);
  const [burstKey, setBurstKey] = useState(0);
  const [shareCopied, setShareCopied] = useState(false);
  const roundLabel = item.round
    ? `Round ${item.round}`
    : (item.current_round ? `Round ${item.current_round}` : null);
  // Card body text. Matchups carry their description in `content` (a
  // string); brackets/pools/tier lists use `description`. Tier lists'
  // `content` is the {tiers} layout OBJECT — never render that as a
  // child (React error #31). Guard to a string.
  const contentText = (typeof item.content === 'string' ? item.content : item.description) || '';
  // Bracket-child matchups need a parent-pointer subtitle. The titles
  // alone ("Round 1 - Match 4") read as gibberish in a flat feed without
  // the bracket name. `bracket_title` isn't always on the wire from the
  // home summary, so fall back to a generic "From a bracket" label.
  const isBracketChild = type === 'matchup' && Boolean(item.bracket_id);
  const parentBracketTitle = item.bracket_title ?? null;

  const handleClick = () => {
    if (type === 'bracket') {
      navigate(`/brackets/${item.id}`);
    } else if (type === 'tierlist') {
      navigate(`/tierlists/${item.id}`);
    } else if (type === 'pool') {
      navigate(`/pools/${item.id}`);
    } else {
      const uid = authorId ?? 'unknown';
      navigate(`/users/${uid}/matchup/${item.id}`);
    }
  };

  // (The detail-page path used to be precomputed here for the share
  // handler. It now lives inside handleClick alone — the share handler
  // builds its absolute URL via buildShareUrl instead.)

  // Stops the click from bubbling up to the card's onClick (which
  // would also navigate to detail). All three action buttons need
  // this; pulled out so each handler can `stop(e); ...` cleanly.
  const stop = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handleLikeClick = async (e) => {
    stop(e);
    if (!viewerId) {
      // Anon viewers used to get a hard redirect to /login here. The
      // fade-background modal is friendlier — keeps the user on the
      // home feed and explains why they need to sign up to like.
      promptUpgrade('like');
      return;
    }
    if (likePending) return;
    setLikePending(true);
    const wasLiked = liked;
    // Optimistic flip. On a failure we roll both pieces of state back.
    setLiked(!wasLiked);
    setLikesCount((c) => Math.max(0, c + (wasLiked ? -1 : 1)));
    if (!wasLiked) setBurstKey((k) => k + 1);
    try {
      if (type === 'bracket') {
        await (wasLiked ? unlikeBracket(item.id) : likeBracket(item.id));
      } else if (type === 'tierlist') {
        await (wasLiked ? unlikeTierList(item.id) : likeTierList(item.id));
      } else {
        await (wasLiked ? unlikeMatchup(item.id) : likeMatchup(item.id));
      }
      track(
        type === 'bracket'
          ? (wasLiked ? 'bracket_unliked' : 'bracket_liked')
          : type === 'tierlist'
          ? (wasLiked ? 'tierlist_unliked' : 'tierlist_liked')
          : (wasLiked ? 'matchup_unliked' : 'matchup_liked'),
        { id: item.id, surface: 'home-feed' },
      );
    } catch (err) {
      console.error('like toggle failed', err);
      setLiked(wasLiked);
      setLikesCount((c) => Math.max(0, c + (wasLiked ? 1 : -1)));
      // The heart used to quietly un-flip with no word why.
      notifyActionFailed(wasLiked ? 'remove your like' : 'like this', err);
    } finally {
      setLikePending(false);
    }
  };

  const handleCommentClick = (e) => {
    stop(e);
    // Comments live on the detail page — clicking here is just a
    // shortcut to "open the matchup so I can post a comment". A
    // dedicated `?focus=comments` query could scroll the textarea
    // into view; saving that for a later iteration.
    handleClick();
  };

  const handleShareClick = async (e) => {
    stop(e);
    // Build the same crawler-aware /m/<short> URL the matchup-page
    // ShareButton uses, so iMessage / Slack / X render the per-matchup
    // composite preview when the link is pasted. Pre-shortID rows fall
    // back to the long SPA URL automatically (see buildShareUrl).
    const rawUrl = buildShareUrl({ item, type });
    const url = withRef(rawUrl, 'home-feed');
    track('content_shared', { id: item.id, type, surface: 'home-feed' });
    // Native share sheet on supporting devices (mobile + a few
    // desktop browsers); falls through to clipboard otherwise so
    // every platform gets some kind of share affordance.
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, url });
        return;
      } catch (err) {
        // User dismissed the sheet or the call rejected — fall
        // through to clipboard so the share still feels useful.
        if (err && err.name === 'AbortError') return;
      }
    }
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        window.setTimeout(() => setShareCopied(false), 1800);
      } catch (err) {
        console.warn('clipboard write failed', err);
      }
    }
  };

  return (
    <article className="home-card" onClick={handleClick}>
      {/* Post header */}
      <div className="home-card__header">
        <span className="home-card__avatar">
          {authorAvatarUrl ? (
            <img
              src={authorAvatarUrl}
              alt=""
              className="home-card__avatar-img"
              loading="lazy"
              decoding="async"
            />
          ) : (
            initial
          )}
        </span>
        <div className="home-card__header-text">
          <span className="home-card__username">{author}</span>
          {timeAgo && <span className="home-card__time">{timeAgo}</span>}
          {/* Community context — when the matchup/bracket lives in a
              community, surface it inline with the byline so the
              viewer can tell at a glance which community a card
              belongs to. Clicking jumps straight to the community
              page. e.stopPropagation prevents the wrapping card-click
              behavior from swallowing the link. */}
          {item.community_slug && (
            <Link
              to={`/c/${item.community_slug}`}
              className="home-card__community"
              onClick={(e) => e.stopPropagation()}
            >
              in /c/{item.community_slug}
            </Link>
          )}
        </div>
        {/*
          Kind/status badge stack. The kind pill ("Matchup" or
          "Bracket") still labels the row's content type; the
          "Completed" pill rides alongside it when status === 'completed'
          so a finished matchup reads as a resolved item rather than
          one you should go vote on. Using a stack instead of replacing
          the kind label keeps the visual taxonomy ("this is a matchup,
          it's done") clear at a glance.
        */}
        <div className="home-card__badges">
          {isCompleted && (
            <span className="home-card__badge home-card__badge--completed">
              Completed
            </span>
          )}
          <span className="home-card__badge">
            {type === 'bracket'
              ? item.format === 'pick_one'
                ? 'This or That'
                : 'Bracket'
              : type === 'pool'
              ? 'Pool'
              : type === 'tierlist' || type === 'tier_list'
              ? 'Tier List'
              : isBracketChild
              ? 'Bracket Match'
              : 'Matchup'}
          </span>
        </div>
      </div>

      {/* Caption */}
      <div className="home-card__caption">
        {isBracketChild && (
          <div className="home-card__breadcrumb">
            <FiCornerDownRight aria-hidden="true" />
            {/* Link straight to the parent bracket. stopPropagation keeps the
                card's own click (→ this matchup) from swallowing it — same
                pattern as the community link above. */}
            {item.bracket_id ? (
              <Link
                to={`/brackets/${item.bracket_id}`}
                className="home-card__breadcrumb-link"
                onClick={(e) => e.stopPropagation()}
              >
                {parentBracketTitle ? `From: ${parentBracketTitle}` : 'From a bracket'}
              </Link>
            ) : (
              <span>
                {parentBracketTitle ? `From: ${parentBracketTitle}` : 'From a bracket'}
              </span>
            )}
          </div>
        )}
        <h3 className="home-card__title">{title}</h3>
        {contentText && (
          <p className="home-card__content">{contentText}</p>
        )}
        {/*
          Per-item vote breakdown — two pills sandwiching a "vs" so
          users see live action ("Take Care 5 · 3 NWTS") instead of
          a flat total. Renders only for two-item matchup cards; bracket
          cards roll up to a child-matchup count instead. Falls through
          to the plain total below when items aren't on the wire (some
          lighter feed shapes don't include them).
        */}
        {itemBreakdownPair ? (
          <div className="home-card__score">
            <span className="home-card__score-side">
              <span className="home-card__score-label">
                {itemBreakdownPair[0].userUsername
                  ? `@${itemBreakdownPair[0].userUsername}`
                  : itemBreakdownPair[0].label}
              </span>
              <span className="home-card__score-votes">
                {itemBreakdownPair[0].votes.toLocaleString()}
              </span>
            </span>
            <span className="home-card__score-divider" aria-hidden="true">vs</span>
            <span className="home-card__score-side home-card__score-side--right">
              <span className="home-card__score-votes">
                {itemBreakdownPair[1].votes.toLocaleString()}
              </span>
              <span className="home-card__score-label">
                {itemBreakdownPair[1].userUsername
                  ? `@${itemBreakdownPair[1].userUsername}`
                  : itemBreakdownPair[1].label}
              </span>
            </span>
          </div>
        ) : null}
        {/*
          Vote count is always shown — including "0 votes" — so the
          card communicates engagement (or lack of it) on every
          render. Previously the meta row hid until votes>0, which
          left brand-new matchups with no signal that votes were
          even a thing on the surface.
        */}
        <div className="home-card__meta">
          <span className="home-card__meta-item">
            {(type === 'tierlist' || type === 'pool')
              ? `${Number(item.saves ?? item.saves_count ?? 0).toLocaleString()} ${Number(item.saves ?? item.saves_count ?? 0) === 1 ? 'save' : 'saves'}`
              : `${votesCount.toLocaleString()} ${votesCount === 1 ? 'vote' : 'votes'}`}
          </span>
          {roundLabel && (
            <span className="home-card__meta-item">{roundLabel}</span>
          )}
        </div>
        <div className="home-card__tags">
          {tags.map((tag) => (
            <span key={tag} className="home-card__tag">#{tag}</span>
          ))}
        </div>
      </div>

      {/* Full-bleed media */}
      <div
        className="home-card__media"
        style={imageUrl ? {} : { background: gradient }}
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt={title}
            className="home-card__media-img"
            loading="lazy"
            decoding="async"
            data-fallback-index="0"
            onError={(e) => {
              // Walk the fallback chain in order (medium, thumb). Each
              // failed step bumps the data attribute so a single bad
              // src doesn't put us in an infinite onError loop. When
              // we've exhausted the chain we clear the src so the
              // gradient background shows through cleanly.
              const idx = Number(e.currentTarget.dataset.fallbackIndex || 0);
              if (idx < imageFallbacks.length) {
                e.currentTarget.dataset.fallbackIndex = String(idx + 1);
                e.currentTarget.src = imageFallbacks[idx];
              } else {
                e.currentTarget.removeAttribute('src');
              }
            }}
          />
        )}
        {/* Tap-to-vote chevron — appears on card hover (CSS) so the
            implicit "whole card is clickable" target gets an explicit
            visual cue. Research feedback flagged the lack of click
            affordance as making cards feel inert. */}
        <span className="home-card__cta" aria-hidden="true">
          <FiArrowRight />
        </span>
      </div>

      {/* Action bar — three real buttons. The card's outer onClick
          still navigates, but each button calls stop(e) so the click
          doesn't bubble up. Like toggles in place; Comment shortcuts
          to the detail page where the textarea lives; Share invokes
          the native share sheet (mobile) or copies to the clipboard
          (desktop fallback) and flips the label to "Copied!" briefly. */}
      <div className="home-card__actions">
        <div className="home-card__action-row">
          {/* Pools are save-based — no like/comment. Show the same card
              chrome but only the Share action for pool cards. */}
          {type !== 'pool' && (
            <>
          <button
            type="button"
            className={`home-card__action home-card__action--like${liked ? ' is-liked' : ''}`}
            onClick={handleLikeClick}
            disabled={likePending}
            aria-pressed={liked}
            aria-label={liked ? 'Unlike' : 'Like'}
          >
            <span className="home-card__action-icon">
              <FiHeart />
              {burstKey > 0 && (
                <span
                  key={burstKey}
                  className="home-card__like-burst"
                  aria-hidden="true"
                >
                  <FiHeart />
                </span>
              )}
            </span>
            {/* Action labels removed — icon + count is enough to read.
                The aria-label on each button still announces the
                action for screen readers. The shareCopied "Copied!"
                feedback is intentionally omitted; the share button
                briefly flashes the green hover state via CSS, which
                provides comparable confirmation without noise. */}
            {likesCount > 0 && <span className="home-card__action-count">{likesCount}</span>}
          </button>
          <button
            type="button"
            className="home-card__action home-card__action--comment"
            onClick={handleCommentClick}
            aria-label="Open to comment"
          >
            <FiMessageCircle />
            {commentsCount > 0 && <span className="home-card__action-count">{commentsCount}</span>}
          </button>
            </>
          )}
          <button
            type="button"
            className={`home-card__action home-card__action--share${shareCopied ? ' is-copied' : ''}`}
            onClick={handleShareClick}
            aria-label="Share"
          >
            <FiShare2 />
          </button>
          {menuActions.length > 0 && (
            <div className="home-card__menu" ref={menuRef}>
              <button
                type="button"
                className="home-card__action home-card__action--menu"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="More actions"
                onClick={(e) => { stop(e); setMenuOpen((v) => !v); }}
              >
                <FiMoreHorizontal />
              </button>
              {menuOpen && (
                <div role="menu" className="home-card__menu-panel">
                  {menuActions.map((action, i) => (
                    <button
                      key={i}
                      type="button"
                      role="menuitem"
                      className={`home-card__menu-item${action.danger ? ' is-danger' : ''}`}
                      onClick={(e) => { stop(e); setMenuOpen(false); action.onClick?.(); }}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default HomeCard;
